import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import {
  Box,
  Pagination as MatPagination,
  PaginationItem,
} from "@mui/material";
import {
  DataGrid as MatDataGrid,
  DataGridProps as MatDataGridProps,
  gridPageCountSelector,
  gridPageSelector,
  GridToolbar,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CustomStyles, getStyles } from "src/styles/theme";
import defaultStyles, { StylesClasses } from "./styles";

const MIN_FILL_HEIGHT = 320;
const FILL_HEIGHT_GUTTER = 24;

const useFillHeight = (enabled: boolean) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const element = ref.current;
    if (!enabled || !element) {
      return;
    }

    const documentTop = element.getBoundingClientRect().top + window.scrollY;
    const next = Math.max(
      MIN_FILL_HEIGHT,
      Math.round(window.innerHeight - documentTop - FILL_HEIGHT_GUTTER)
    );

    setHeight((prev) => (prev === next ? prev : next));
  }, [enabled]);

  useLayoutEffect(measure);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return { ref, height: enabled ? height : null };
};

type DataGridProps = MatDataGridProps & {
  toolbar?: boolean;
  emptyTable?: string;
  customStyles?: CustomStyles<StylesClasses>;
  hidePagination?: boolean;
  pageNumber?: number;
  onChangePage?: (v: number) => void;
  fillHeight?: boolean;
};

const SortIconAsc = () => {
  const styles = getStyles<StylesClasses>(defaultStyles);
  return (
    <Box {...styles("iconStyles")}>
      <ArrowDropDownRoundedIcon fontSize="large" />
    </Box>
  );
};

const SortIconDesc = () => {
  const styles = getStyles(defaultStyles);
  return (
    <Box {...styles("iconStyles")}>
      <ArrowDropUpRoundedIcon fontSize="large" />
    </Box>
  );
};

const CustomPagination = ({
  rowsPerPageOptions,
  rowCount,
  hideFooterPagination,
  pageNumber,
  onChangePage,
}) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const styles = getStyles<StylesClasses>(defaultStyles);

  return (
    <Box {...styles("paginationWrapper")}>
      {!hideFooterPagination && (
        <MatPagination
          color="primary"
          count={pageCount || 1}
          {...styles("pagination")}
          page={pageNumber ? pageNumber : page + 1}
          onChange={(event, value) => {
            if (pageNumber && onChangePage) {
              onChangePage(value - 1);
            } else {
              apiRef.current.setPage(value - 1);
            }
          }}
          renderItem={(item) => (
            <PaginationItem {...styles("paginationItem")} {...item} />
          )}
        />
      )}
    </Box>
  );
};

const DataGrid = ({
  rows,
  columns,
  autoHeight = true,
  checkboxSelection = false,
  disableColumnFilter = true,
  disableColumnMenu = true,
  hideFooterPagination = false,
  loading = false,
  toolbar = false,
  customStyles = {},
  rowsPerPageOptions = [5],
  rowCount = 5,
  hidePagination = false,
  emptyTable = "No Data Available",
  paginationMode = "server",
  pageNumber,
  onChangePage,
  fillHeight = true,
  ...rest
}: DataGridProps) => {
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const { ref, height } = useFillHeight(fillHeight && rows.length > 0);
  const isFilled = height !== null;

  return (
    <Box
      ref={ref}
      {...styles(
        isFilled ? ["dataGridWrapper", "fillHeightWrapper"] : "dataGridWrapper",
        {
          height:
            rows.length === 0 ? "300px" : isFilled ? `${height}px` : "auto",
        }
      )}
    >
      <MatDataGrid
        {...styles("grid")}
        rows={rows}
        columns={columns}
        autoHeight={isFilled ? false : autoHeight}
        checkboxSelection={checkboxSelection}
        disableColumnFilter={disableColumnFilter}
        disableColumnMenu={disableColumnMenu}
        loading={loading}
        sortingOrder={["asc", "desc"]}
        pagination
        density="standard"
        rowsPerPageOptions={rowsPerPageOptions}
        rowCount={rowCount}
        hideFooterPagination={hideFooterPagination}
        paginationMode={paginationMode}
        columnBuffer={20}
        components={{
          NoResultsOverlay: () => <Box></Box>,
          ColumnResizeIcon: () => null,
          ColumnSortedDescendingIcon: SortIconDesc,
          ColumnSortedAscendingIcon: SortIconAsc,
          Footer: () =>
            CustomPagination({
              rowsPerPageOptions,
              rowCount,
              hideFooterPagination,
              pageNumber,
              onChangePage,
            }),

          Toolbar: toolbar && GridToolbar,
        }}
        {...rest}
      />
    </Box>
  );
};

export default DataGrid;
